import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import SearchBar from '../components/SearchBar'
import Newsletter from '../components/Newsletter'
import CodeCard from '../components/CodeCard'
import Faq from '../components/Faq'
import SchemaMarkup from '../components/SchemaMarkup'
import supabase from '../services/supabase'
import '../styles/global.css'

const DirectoryIaePage = () => {
  const [iaeCodes, setIaeCodes] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [limit, setLimit] = useState(20)
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const navigate = useNavigate()

  const faqData = [
    {
      question: '¿Qué es el IAE?',
      answer:
        'El IAE (Impuesto de Actividades Económicas) es un tributo que deben pagar las empresas y autónomos que realizan una actividad económica en España. Este impuesto se calcula en función de los ingresos y la actividad que desarrolle cada empresa o autónomo, y se clasifica mediante un código único llamado código IAE, que permite identificar la actividad económica específica.',
    },
    {
      question: '¿Cómo encontrar el código IAE de mi actividad económica?',
      answer:
        'Para encontrar el código IAE correspondiente a tu actividad económica, puedes utilizar nuestro conversor de códigos IAE. Simplemente busca la actividad que realizas en el buscador y te mostraremos el código IAE relacionado, junto con una descripción detallada de la actividad. Además, si necesitas convertirlo a un código CNAE, también lo podrás hacer de forma rápida y sencilla.',
    },
    {
      question: '¿Cuál es la diferencia entre los códigos IAE y CNAE?',
      answer:
        'El IAE es un código utilizado en España para clasificar las actividades económicas a efectos fiscales, mientras que el CNAE (Clasificación Nacional de Actividades Económicas) es utilizado por el INE (Instituto Nacional de Estadística) para estadísticas nacionales y en algunos trámites administrativos. Aunque ambos sistemas clasifican actividades económicas, el IAE se enfoca más en el ámbito fiscal y el CNAE tiene un alcance más amplio.',
    },
    {
      question: '¿Cómo puedo saber si debo pagar el IAE?',
      answer:
        'El IAE debe ser pagado por las empresas y autónomos cuyo volumen de negocio supere el umbral establecido por la ley. Existen algunas exenciones, como para aquellos que facturan por debajo de un umbral específico o si la actividad no está sujeta a este impuesto. Para saber si estás obligado a pagar el IAE, consulta con un asesor fiscal o revisa la normativa vigente en España.',
    },
    {
      question:
        '¿Puedo cambiar mi código IAE si cambio de actividad económica?',
      answer:
        'Sí, si decides cambiar tu actividad económica, deberás actualizar tu código IAE en la Agencia Tributaria. Es importante que notifiques cualquier cambio en tu actividad para que se refleje correctamente en tus declaraciones fiscales y para evitar problemas futuros con el fisco.',
    },
    {
      question:
        '¿Es necesario un código IAE para todos los negocios en España?',
      answer:
        'Sí, el código IAE es necesario para todos los negocios y actividades económicas que estén registrados en el sistema fiscal español, ya sea una actividad industrial, comercial o de servicios. Sin embargo, existen algunas excepciones y exenciones, por lo que es recomendable consultar a un asesor fiscal o revisar la normativa vigente para determinar si tu actividad está obligada a tener un código IAE.',
    },
    {
      question: '¿Qué es el certificado IAE?',
      answer:
        'El certificado IAE es un documento oficial que acredita que una empresa o autónomo está inscrito en el registro del Impuesto de Actividades Económicas (IAE). Este certificado puede ser requerido en diversos trámites administrativos o para formalizar la actividad económica de un negocio ante organismos públicos o privados. En él, se detallan los códigos IAE asociados a la actividad económica que realiza el contribuyente, así como su alta en el sistema tributario.',
    },
    {
      question: '¿Cómo puedo obtener el certificado IAE?',
      answer:
        'Para obtener el certificado IAE, debes solicitarlo a través de la Agencia Tributaria. El proceso se realiza de manera online, accediendo a tu sede electrónica y utilizando tu certificado digital o cl@ve. También puedes realizar la solicitud en persona en las oficinas de la Agencia Tributaria. Una vez que se emita, recibirás el certificado con los códigos IAE correspondientes a tu actividad económica registrada.',
    },
  ]

  const fetchIaeCodes = async (
    limitParam = limit,
    offsetParam = offset,
    append = false
  ) => {
    try {
      setIsLoading(true)
      const { data, error } = await supabase.rpc(
        'get_most_searched_iae_codes',
        {
          limit_param: limitParam,
          offset_param: offsetParam,
        }
      )

      if (error) throw error

      if (!data || data.length === 0) {
        console.warn('No IAE codes found')
        setHasMore(false)
        if (!append) {
          setIaeCodes([])
        }
        return
      }

      // If we're appending, add to existing codes, otherwise replace
      if (append) {
        setIaeCodes((prevCodes) => [...prevCodes, ...data])
      } else {
        setIaeCodes(data)
      }

      // Check if we have more data to load
      setHasMore(data.length === limitParam)
    } catch (error) {
      console.error('Error fetching IAE codes:', error)
      setError('Error al cargar los códigos IAE')
    } finally {
      setIsLoading(false)
    }
  }

  // Load more codes
  const handleLoadMore = () => {
    const newOffset = offset + limit
    setOffset(newOffset)
    fetchIaeCodes(limit, newOffset, true)
  }

  useEffect(() => {
    fetchIaeCodes()
  }, [])

  return (
    <div className='container mx-auto px-4'>
      {/* Header Section */}
      <header className='py-8 md:py-16 text-center'>
        <h1 className='text-2xl md:text-4xl font-bold mb-4 text-text-primary'>
          Listado de Epígrafes IAE Actualizado
        </h1>
        <p className='text-text-secondary text-lg mb-8 max-w-2xl mx-auto'>
          Explora la base de datos completa de epígrafes IAE (Impuesto sobre
          Actividades Económicas). Encuentra, filtra y consulta en tiempo real
          los epígrafes oficiales con sus descripciones detalladas y
          equivalencias CNAE.
        </p>

        {/* Search Bar */}
        <div className='max-w-xl mx-auto mb-6'>
          <SearchBar
            type='iae'
            hideConvertButton={true}
            onCodeSelect={(code) => {
              // Redirect to details page when a code is selected
              navigate(`/iae/${code.code.replace('.', '')}/${code.slug}`)
            }}
          />
        </div>
      </header>

      {/* IAE Codes Section */}
      <section className='py-8 md:py-12'>
        <h2 className='text-xl md:text-3xl font-bold mb-6 text-text-primary text-center'>
          Epígrafes IAE Más Populares
        </h2>

        {/* SearchResultsPage Schema */}
        {iaeCodes.length > 0 && (
          <SchemaMarkup
            type='SearchResultsPage'
            data={{
              results: iaeCodes.map((code) => ({
                title: `${code.cod_iae} - ${code.titulo_iae}`,
                url: `https://conversoriaecnae.es/iae/${code.cod_iae.replace(
                  '.',
                  ''
                )}/${code.slug_iae}`,
              })),
            }}
          />
        )}

        {/* ItemList Schema */}
        {iaeCodes.length > 0 && (
          <SchemaMarkup
            type='ItemList'
            data={{
              name: 'Epígrafes IAE Más Populares',
              description: 'Lista de los epígrafes IAE más buscados en España',
              items: iaeCodes.map((code) => ({
                title: `${code.cod_iae} - ${code.titulo_iae}`,
                code: code.cod_iae,
                url: `https://conversoriaecnae.es/iae/${code.cod_iae.replace(
                  '.',
                  ''
                )}/${code.slug_iae}`,
              })),
            }}
          />
        )}

        {isLoading && iaeCodes.length === 0 ? (
          <div className='flex justify-center'>
            <div className='w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin'></div>
          </div>
        ) : error ? (
          <div className='text-center text-red-500'>{error}</div>
        ) : (
          <>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
              {iaeCodes.map((code, index) => (
                <CodeCard
                  key={index}
                  code={code.cod_iae}
                  title={code.titulo_iae}
                  type='iae'
                  slug={code.slug_iae}
                  searchCount={code.cant_busquedas_iae}
                />
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && iaeCodes.length > 0 && (
              <div className='flex justify-center mt-8'>
                <button
                  onClick={handleLoadMore}
                  className='px-6 py-2 bg-surface text-white rounded-md hover:bg-primary transition-colors duration-300 flex items-center'
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className='mr-2'>Cargando...</span>
                      <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin'></div>
                    </>
                  ) : (
                    'Cargar Más Códigos'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Newsletter Section */}
      <Newsletter />

      {/* FAQ Section */}
      <Faq
        title='Preguntas Frecuentes sobre Epígrafes IAE (FAQ)'
        description='Resolvemos tus dudas sobre los epígrafes IAE, su aplicación y cómo utilizarlos correctamente para tu actividad económica. Información actualizada según las últimas disposiciones oficiales.'
        faqData={faqData}
      />
    </div>
  )
}

export default DirectoryIaePage
